import type { Placement } from "@floating-ui/react"
import { floating, radiusCva, shadowCva, useProvider } from "@overmap-ai/blocks"
import { FloatingMenu as TipTapFloatingMenu } from "@tiptap/react/menus"
import { cx } from "class-variance-authority"
import { type ComponentProps, memo, useMemo } from "react"

import type { OptionalEditorProps } from "../../components"
import { useFallbackEditor } from "../../hooks"
import {
	FLOATING_MENU_CONTENT_PADDING,
	FLOATING_MENU_MINIMUM_MAX_HEIGHT,
	FLOATING_MENU_OVERFLOW_PADDING,
} from "./constants"
import type { FloatingMenuAlignment, FloatingMenuSide } from "./typings"

type TipTapFloatingMenuProps = ComponentProps<typeof TipTapFloatingMenu>

interface FloatingMenuRootProps
	extends Omit<TipTapFloatingMenuProps, "appendTo" | "options" | "editor">, OptionalEditorProps {
	offset?: number
	side?: FloatingMenuSide
	align?: FloatingMenuAlignment
}

export const FloatingMenu = memo<FloatingMenuRootProps>((props) => {
	const {
		ref,
		children,
		className,
		side = "right",
		align = "center",
		offset = 5,
		// TipTapFloatingMenu props
		editor: editorFromProps,
		...rest
	} = props

	const { radius } = useProvider()
	const editor = useFallbackEditor(editorFromProps)

	const tipTapFloatingMenuOptions = useMemo<TipTapFloatingMenuProps["options"]>(
		() => ({
			strategy: "absolute",
			// TODO: probably some way with template string types to not need the "as Placement"
			placement: `${side}${align !== "center" ? "-" : ""}${align !== "center" ? align : ""}` as Placement,
			offset: { mainAxis: offset },
			size: {
				apply({ availableHeight, elements }) {
					elements.floating.style.setProperty(
						"--overmap-menu-available-height",
						`${Math.max(availableHeight, FLOATING_MENU_MINIMUM_MAX_HEIGHT)}px`,
					)
				},
				padding: FLOATING_MENU_OVERFLOW_PADDING,
			},
			flip: { fallbackStrategy: "initialPlacement" },
			shift: { padding: FLOATING_MENU_CONTENT_PADDING },
			hide: true,
		}),
		[align, offset, side],
	)

	return (
		<TipTapFloatingMenu
			ref={ref}
			className={cx(
				[
					"bg-(--color-background)",
					"dark:bg-(--base-2)",
					"ring-1",
					"ring-(--base-6)",
					"overflow-hidden",
					"relative",
					"max-h-(--overmap-menu-available-height)",
				],
				radiusCva({ radius, maxLarge: true }),
				floating({ side }),
				shadowCva({ size: "md" }),
				className,
			)}
			// TipTapFloatingMenu props
			editor={editor}
			appendTo={document.body}
			options={tipTapFloatingMenuOptions}
			data-floating-content=""
			{...rest}
		>
			{children}
		</TipTapFloatingMenu>
	)
})
FloatingMenu.displayName = "FloatingMenuRoot"
